<template>
  <v-flex xs12 lg9>
    <v-card class="px-2 py-3">
      <v-flex xs12 px-3 pb-3 v-if="err">
        <v-alert :value="true" type="error">{{err}}</v-alert>
      </v-flex>
      <v-flex xs12 px-3 pb-3 v-if="success">
        <v-alert :value="true" type="success">{{success}}</v-alert>
      </v-flex>
      <v-form v-model="valid">
        <v-layout row wrap justify-space-between align-center>
          <v-flex xs12 sm6 px-3>
            <v-autocomplete
              :items="mappedCompanies"
              :label="$lang.labels.company"
              v-model="license.companyId"
              :readonly="isView"
              required
              :rules="[v => !!v || `${$lang.errors.missingCompany}`]"
            />
          </v-flex>
          <v-flex xs12 sm6 px-3>
            <v-autocomplete
              :items="licenseTypes"
              :label="$lang.header.licenseType"
              v-model="license.licenseType"
              :readonly="isView"
              required
              :rules="[v => !!v || `${$lang.errors.missingLicenseType}`]"
            />
          </v-flex>
          <v-flex xs12 sm6 px-3>
            <v-text-field
              type="number"
              min="0"
              :readonly="isView || testLicense"
              :label="$lang.labels.concurrent"
              v-model="license.licenseAttributes.concurrentUsers"
              required
              :rules="[v => !!v || `${$lang.errors.missingConcurrent}`]"
            />
          </v-flex>
          <v-flex xs12 sm6 px-3>
            <v-text-field
              :readonly="isView"
              :label="$lang.labels.sunLicense"
              v-model="license.sunLicenseSystemVersion"
              required
              :rules="[v => !!v || `${$lang.errors.missingSunLicense}`]"
            />
          </v-flex>
          <v-flex xs12 sm6 px-3>
            <v-autocomplete
              clearable
              :readonly="isView"
              :items="instances"
              :label="$lang.header.moonInstName"
              v-model="license.moonInstanceId"
              item-text="name"
              item-value="instanceId"
            />
          </v-flex>
          <v-flex xs12 sm6 px-3 v-if="isView">
            <v-text-field
              :readonly="isView"
              :label="$lang.labels.licenseSyncStatus"
              v-model="license.licenseSyncStatus"
            />
          </v-flex>
          <v-flex xs12 px-3 v-if="isView">
            <v-textarea
              :readonly="isView"
              :label="$lang.labels.moonInstID"
              v-model="license.moonInstanceId"
              auto-grow
              rows="1"
            />
          </v-flex>
          <v-layout row wrap justify-space-between>
            <v-flex xs12 sm6 px-3>
              <v-menu
                ref="menu"
                :close-on-content-click="false"
                v-model="menu"
                :nudge-right="40"
                :return-value.sync="license.getsActiveOn"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
                :disabled="isView"
              >
                <v-text-field
                  required
                  readonly
                  :rules="[v => !!v || `${$lang.errors.missingActivationDate}`]"
                  slot="activator"
                  v-model="license.getsActiveOn"
                  :label="$lang.header.activationDate"
                  prepend-icon="event"
                />
                <v-date-picker v-model="license.getsActiveOn" @input="$refs.menu.save(license.getsActiveOn)" no-title scrollable color="primary" />
              </v-menu>
            </v-flex>
            <v-flex xs12 sm6 px-3>
              <v-menu
                ref="menu2"
                :close-on-content-click="false"
                v-model="menu2"
                :nudge-right="40"
                :return-value.sync="license.expiresOn"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
                :disabled="isView || testLicense"
              >
                <v-text-field
                  :rules="[v => !!v || `${$lang.errors.missingExpiryDate}`]"
                  readonly
                  slot="activator"
                  v-model="license.expiresOn"
                  :label="$lang.header.expiryDate"
                  prepend-icon="event"
                  required
                />
                <v-date-picker v-model="license.expiresOn" @input="$refs.menu2.save(license.expiresOn)" no-title scrollable color="primary"/>
              </v-menu>
            </v-flex>
          </v-layout>
        </v-layout>
        <v-layout row wrap justify-space-between>
          <v-flex xs12 sm6 px-3>
            <v-text-field
              v-if="isView && createdOn"
              :label="$lang.labels.created"
              v-model="createdOn"
              readonly
            />
          </v-flex>
          <v-flex xs12 sm6 px-3>
            <v-text-field
              v-if="isView && modifiedOn"
              :label="$lang.labels.modified"
              v-model="modifiedOn"
              readonly
            />
          </v-flex>
        </v-layout>
        <v-layout row wrap justify-space-between align-center class="px-2">
          <v-btn 
            class="mx-2"
            flat 
            @click="$router.push({ name: 'licenses' })">
            <v-icon>keyboard_arrow_left</v-icon>
            {{$lang.actions.close}}
          </v-btn>
          <v-btn class="primary mx-2" v-if="isView" @click="viewMiniLicense = true">{{$lang.actions.viewMiniLicense}}</v-btn>
          <v-btn class="primary mx-2" v-else :disabled="!valid || creating" @click="submit">{{$lang.actions.create}}</v-btn>
        </v-layout>
      </v-form>
    </v-card>
    <v-dialog v-if="viewMiniLicense" v-model="viewMiniLicense" max-width="800">
      <mini-license-modal :license="license" @close="viewMiniLicense = false"/>
    </v-dialog>
    <v-dialog v-if="showActivationModal" persistent v-model="showActivationModal" max-width="500">
      <activation-modal :licenseId="activationData" @close="showActivationModal = false"/>
    </v-dialog>
  </v-flex>
</template>

<script>
  import miniLicenseModal from '@/components/modals/MiniLicense';
  import {DateTime} from 'luxon';

  export default {
    components: {
      miniLicenseModal,
      activationModal: () => import('../modals/LicenseActivationModal'),
    },
    data: () => ({
      err: '',
      success: '',
      viewMiniLicense: false,
      miniLicense: null,
      valid: false,
      isView: false,
      menu: false,
      menu2: false,
      companies: [],
      licenseTypes: [],
      instances: [],
      license: {
        companyId: null,
        licenseType: null,
        moonInstanceId: null,
        licenseAttributes: {
          concurrentUsers: '',
        },
        sunLicenseSystemVersion: '',
        getsActiveOn: '',
        expiresOn: '',
        isActivateOn: ''
      },
      testLicense: false,
      createdOn: '',
      modifiedOn: '',
      showActivationModal: false,
      creating: false,
      activationData: null,
    }),
    watch: {
      'license.licenseType': function (val) {
        immediate: false;
        deep: true;
        if (this.isView) {
          return;
        }
        if (val === 'test') {
          this.license.sunLicenseSystemVersion = '';
          this.license.getsActiveOn = DateTime.local().toFormat('yyyy-MM-dd');
          this.license.expiresOn = DateTime.local().plus({ days: 60 }).toFormat('yyyy-MM-dd');
          this.testLicense = true;
          this.license.licenseAttributes.concurrentUsers = '10';
        }
        if (val === 'production') {
          this.license.sunLicenseSystemVersion = '1';
          this.license.getsActiveOn = DateTime.local().toFormat('yyyy-MM-dd');
          this.license.expiresOn = DateTime.local().plus({ years: 99 }).toFormat('yyyy-MM-dd');;
          this.testLicense = false;
          this.license.licenseAttributes.concurrentUsers = '';
        }
      },
    },
    computed: {
      mappedCompanies() {
        return this.companies.map(company => ({
          text: `${company.name} (${company.companyStatus})`,
          value: company.id,
        }));
      },
    },
    methods: {
      formatDate(date) {
        return DateTime.fromISO(date).toFormat('yyyy-MM-dd HH:mm:ss');
      },
      submit() {
        this.creating = true;
        this.license.isActiveOn = this.license.getsActiveOn;
        this.$http.post('license/create', this.license)
        .then((res) => {
          if (res.body.status === 'Success') {
            this.success = 'License created successfuly';
            setTimeout(() => {
              this.activationData = res.body.licenseId;
              this.showActivationModal = true;
            }, 1000);
            this.creating = false;
            return;
          }
          this.err = res.body.statusText;
        }, (err) => {
          this.creating = false;
          this.err = err.body.statusText || err.body.message;
        });
      },
      init() {
        return new Promise((resolve) => {
          this.$http.get('company')
        .then((res) => {
          resolve()
          if (res.body.status === 'Success') {
            this.companies = res.body.companies;
            return;
          }
          this.err = res.body.statusText;
        }, (err) => {
          resolve()
          this.err = err.body.statusText || err.body.message;
        });
      
        this.$http.get('license/fetchlicensetypes')
          .then((res) => {
            resolve()
            if (res.body.status === 'Success') {
              this.licenseTypes = res.body.types;
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            resolve()
            this.err = err.body.statusText || err.body.message;
          });

        this.$http.get('moon/instance')
          .then((res) => {
            resolve()
            if (res.body.status === 'Success') {
              const instances = res.body.details;
              this.instances = instances.sort((a, b) => {
                var nameA = a.name.toUpperCase();
                var nameB = b.name.toUpperCase();
                if (nameA < nameB) {
                  return -1;
                }
                if (nameA > nameB) {
                  return 1;
                }
                return 0;
              }).filter((instance) => instance.status === 'active');
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            resolve()
            this.err = err.body.statusText || err.body.message;
          });

        });
      },
    },
    created() {
      this.init()
        .then(() => {
          if (this.$route.params.id) {
            this.isView = true;
            this.$http.get(`license/${this.$route.params.id}`)
              .then((res) => {
                if (res.body.status === 'Success') {
                  const license = res.body.license;
                  license.getsActiveOn = this.formatDate(license.getsActiveOn);
                  license.expiresOn = this.formatDate(license.expiresOn);
                  this.createdOn = this.formatDate(license.createdOn);
                  if (license.modifiedOn) {
                    this.modifiedOn = this.formatDate(license.modifiedOn);
                  }
                  this.license = license;
                  return;
                }
                this.err = res.body.statusText;
              }, (err) => {
                this.err = err.body.statusText || err.body.message;
              });
          }
        });
      },
  };
</script>


// WEBPACK FOOTER //
// LicenseDetails.vue?06cc3c34